// Homepage "Founding Customer Pricing" banner. LIVE from the FOUNDER promo in the DB
// (usePromoLive) — renders nothing the moment an admin deactivates FOUNDER. See promoConfig.js.
import { Link } from "react-router-dom";
import { Tag, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { PROMO, usePromoLive, buildPromoView, trackPromo } from "@/lib/promoConfig";

// Loading placeholder — same box so the real banner drops in with no layout shift.
function PromoBannerSkeleton() {
  return (
    <section
      className="relative z-40 animate-pulse border-b border-aqua/25 bg-gradient-to-r from-abyss via-abyss-alt to-abyss pt-24 pb-6 md:pt-28 md:pb-7"
      data-testid="promo-banner-skeleton"
      aria-hidden="true"
    >
      <div className="mx-auto flex max-w-[1600px] flex-col gap-5 px-6 md:flex-row md:items-center md:justify-between md:px-10">
        <div className="w-full max-w-3xl">
          <div className="mb-3 h-6 w-48 rounded-full bg-white/10" />
          <div className="h-8 w-2/3 rounded-md bg-white/10 md:h-10" />
          <div className="mt-3 h-3.5 w-1/3 rounded bg-white/10" />
          <div className="mt-3 h-3.5 w-3/4 rounded bg-white/10" />
          <div className="mt-3 h-8 w-72 rounded-md bg-white/10" />
        </div>
        <div className="h-12 w-56 shrink-0 self-start rounded-full bg-white/10 md:self-center" />
      </div>
    </section>
  );
}

export default function PromoBanner() {
  const { lang } = useLang();
  const { loading, active, promo } = usePromoLive();
  if (loading) return <PromoBannerSkeleton />;
  if (!active) return null;

  const { text: tx } = buildPromoView(promo, lang);

  return (
    <section
      className="relative z-40 border-b border-aqua/25 bg-gradient-to-r from-abyss via-abyss-alt to-abyss pt-24 pb-6 md:pt-28 md:pb-7"
      data-testid="promo-banner"
      aria-label={tx.label}
    >
      <div className="mx-auto flex max-w-[1600px] flex-col gap-5 px-6 md:flex-row md:items-center md:justify-between md:px-10">
        <div className="max-w-3xl">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-white shadow" data-testid="promo-banner-label">
            <Tag className="h-3.5 w-3.5" />
            {tx.label}
          </div>
          <h2 className="font-display text-2xl font-black uppercase leading-tight text-white md:text-4xl" data-testid="promo-banner-heading">
            <span className="text-aqua">{tx.bannerPrice}</span>
          </h2>
          <p className="mt-1 text-sm font-semibold text-white/60 line-through decoration-white/40" data-testid="promo-banner-regular">
            {tx.bannerRegular}
          </p>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/75" data-testid="promo-banner-text">
            {tx.availability}{" "}
            <Link to={PROMO.pagePath} className="font-semibold text-aqua underline-offset-2 hover:underline" data-testid="promo-banner-details">
              {tx.details}
            </Link>
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm md:text-base" data-testid="promo-banner-code">
            <span className="font-semibold text-white">{tx.codeLead}</span>
            <code className="rounded-md border border-aqua/50 bg-aqua/15 px-3 py-1 font-display text-lg font-black tracking-widest text-aqua">
              {PROMO.code}
            </code>
            <span className="font-semibold text-white">{tx.codeTail}</span>
          </div>
        </div>
        <a
          href={PROMO.orderPath}
          onClick={() => trackPromo("promo_banner_cta_click", { location: "homepage_banner", destination: PROMO.orderPath })}
          className="inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-aqua px-7 py-3.5 text-sm font-bold text-abyss transition-transform duration-300 hover:scale-105 active:scale-95 glow-aqua md:self-center"
          data-testid="promo-banner-cta"
          data-analytics="promo-banner-cta"
        >
          {tx.cta}
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </section>
  );
}
